import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import styles from './MissionList.module.scss';

import actions from '@/actions';
import { PrimaryButton } from '@/components/ui/kit/Button';
import MissionPreview from '@/components/ui/MissionPreview';
import MissionTable from '@/components/ui/MissionTable';
import ModalWrapper from '@/components/ui/ModalWrapper';
import { getCommandForSurveySegment } from '@/helpers/CommandConverter';
import { getMission } from '@/helpers/Requester';
import { modalManager } from '@/libs/ModalContainer';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

export const PURPOSE = {
  GUIDE: Symbol('GUIDE'),
  UPLOAD: Symbol('UPLOAD'),
};

const Modal = ({ purpose }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [selectedMission, setSelectedMission] = useState();

  const robot = useMemo(() => {
    const targetRobotIds = store.getState().robot?.targetRobotIds;
    // targetRobotIds 없는 경우 (e.g. purpose: GUIDE)
    if (!targetRobotIds) return;

    return store.getState().robots.find(({ id }) => id === targetRobotIds[0]);
  }, []);

  const handleSelect = (mission) => {
    // 미션 선택 해제된 경우 (예: 미션 삭제)
    if (!mission) {
      setSelectedMission(null);
      return;
    }

    getMission(mission.id).then(({ success, data }) => {
      if (success) {
        const mission = structuredClone(data);
        mission.json.commands = mission.json.commands.map((command) => getCommandForSurveySegment(command));
        setSelectedMission(mission);
      }
    });
  };

  const close = () => {
    modalManager.hide();
  };

  const doLoad = () => {
    if (!selectedMission) {
      notifier.error('Please select a mission.');
      return;
    }

    // 가이드용
    if (purpose === PURPOSE.GUIDE) {
      const isAdded = store.getState().missions.some(({ id }) => id === selectedMission.id);
      if (isAdded) {
        notifier.error('This mission is already added.');
        return;
      }

      dispatch(actions.missions.add(selectedMission));
    }
    // 업로드용
    else if (purpose === PURPOSE.UPLOAD) {
      const commands = [];
      selectedMission.json.commands.forEach((command) => {
        switch (command.type) {
          case 'navWaypoint':
            commands.push(command);

            // 촬영 명령 위치인 경우
            if (Object.hasOwn(command.data, 'camera')) {
              // 촬영 시작점 여부
              const isStartToShoot = command.data.camera.shoot;
              const id = `${command.id}/camera/` + (isStartToShoot ? 'start' : 'stop');
              const distance = command.data.camera.interval;

              commands.push({ id, type: 'doSetCamTriggDist', data: { distance } });
            }
            break;

          case 'cusSurvey':
            command.data.positions.forEach((position, index) => {
              commands.push({
                id: `${command.id}/${index}`,
                type: 'navWaypoint',
                data: {
                  position: { lat: position.lat, lng: position.lng },
                  altitude: command.data.altitude,
                },
              });

              // 촬영 명령 위치인 경우
              if (Object.hasOwn(position, 'shoot')) {
                // 촬영 시작점 여부
                const isStartToShoot = position.shoot;
                const id = `${command.id}/${index}/camera/` + (isStartToShoot ? 'start' : 'stop');
                const distance = isStartToShoot ? command.data.camera.interval : 0;

                commands.push({ id, type: 'doSetCamTriggDist', data: { distance } });
              }
            });
            break;

          default:
            commands.push(command);
            break;
        }
      });

      const mission = {
        id: selectedMission.id,
        commands,
        fences: selectedMission.json.fences,
      };

      dispatch(actions.missions.load(robot.id, mission));
    }

    close();
  };

  return (
    <ModalWrapper className={cx('container')}>
      <div className={cx('wrapper')}>
        <MissionTable onSelectMission={handleSelect} showMissionEdit={purpose === PURPOSE.UPLOAD} />
        <MissionPreview mission={selectedMission} />
      </div>
      <div className={cx('bottom')}>
        <PrimaryButton text="Cancel" onClick={close} />
        <PrimaryButton text="Load" accent onClick={doLoad} />
      </div>
    </ModalWrapper>
  );
};

export default Modal;
